/* Turns a stream of pitch frames into discrete note events (plucks or sung notes). */
import { median } from './math';

export type TrackerMode = 'guitar' | 'voice';

export interface TrackerOptions {
  mode: TrackerMode;
  a4: number;
  gate: number;
  minClarity: number;
  stableMs: number;
  minFrames: number;
  /** Level jump over the decaying envelope that counts as a new pluck (0 disables). */
  onsetRatio: number;
  /** Level dip relative to the recent peak that marks a re-articulated sung note (0 disables). */
  dipRatio: number;
  envHalfLife: number;
  refractory: number;
  silenceMs: number;
  maxDevCents: number;
}

export interface TrackerFrame {
  /** seconds */
  t: number;
  rms: number;
  freq: number;
  clarity: number;
  /** Level of the newest block after a first difference (pluck detector). */
  hf?: number;
}

export interface NoteEvent {
  midi: number;
  cents: number;
  freq: number;
  t: number;
}

export class NoteTracker {
  o: TrackerOptions;
  private cand: number | null = null;
  private candSince = 0;
  private vals: number[] = [];
  private last: number | null = null;
  private onset = false;
  private lastOnsetT = -1;
  private silentSince: number | null = null;
  private env = 0;
  private lastT: number | null = null;
  private peak = 0;
  private dipped = false;
  private armed = false;
  private warming = false;

  constructor(opts: Partial<TrackerOptions> = {}) {
    const voice = opts.mode === 'voice';
    this.o = {
      mode: 'guitar',
      a4: 440,
      gate: 0.01,
      minClarity: voice ? 0.8 : 0.86,
      stableMs: voice ? 150 : 55,
      minFrames: voice ? 5 : 3,
      onsetRatio: voice ? 0 : 1.8,
      dipRatio: voice ? 0.35 : 0,
      envHalfLife: 0.12,
      refractory: 0.1,
      silenceMs: voice ? 45 : 140,
      maxDevCents: voice ? 50 : 42,
      ...opts,
    };
    this.reset();
  }

  /** From now on a note counts only after a new onset (a fresh pluck): a string that is already ringing
      (the previous answer, or a note played while the app's own sound paused listening) is not a new note.
      The next frame only measures the level an onset is compared with. Guitar mode (needs onsets). */
  arm(): void {
    this.armed = true;
    this.warming = true;
  }

  /** Starts over after a pause (the level history is gone). An armed tracker stays armed, but the first frame
      after the pause may be an onset again: the caller re-arms when the input did not come back louder. */
  reset(): void {
    this.warming = false;
    this.cand = null;
    this.candSince = 0;
    this.vals = [];
    this.last = null;
    this.onset = false;
    this.lastOnsetT = -1;
    this.silentSince = null;
    this.env = 0;
    this.lastT = null;
    this.peak = 0;
    this.dipped = false;
  }

  push(fr: TrackerFrame): NoteEvent | null {
    const o = this.o;
    const t = fr.t;
    const dt = this.lastT == null ? 0.016 : Math.max(0.001, t - this.lastT);
    this.lastT = t;
    const lev = fr.hf != null ? fr.hf : fr.rms;
    const prevEnv = this.env;
    this.env = Math.max(lev, this.env * 0.5 ** (dt / o.envHalfLife));
    const warming = this.warming;
    this.warming = false;
    if (o.dipRatio > 0) {
      // re-articulation: level dips well below the recent peak, then comes back
      this.peak = Math.max(fr.rms, this.peak * 0.5 ** (dt / 0.5));
      if (fr.rms < this.peak * o.dipRatio) this.dipped = true;
      else if (this.dipped && fr.rms > this.peak * 0.6 && fr.rms >= o.gate) {
        this.dipped = false;
        this.onset = true;
        this.cand = null;
        this.vals = [];
      }
    }
    if (fr.rms < o.gate) {
      if (this.silentSince === null) this.silentSince = t;
      if (t - this.silentSince >= o.silenceMs / 1000) this.last = null;
      this.cand = null;
      this.vals = [];
      return null;
    }
    this.silentSince = null;
    if (o.onsetRatio > 0 && !warming && lev > prevEnv * o.onsetRatio && t - this.lastOnsetT > o.refractory) {
      this.onset = true;
      this.armed = false;
      this.lastOnsetT = t;
      this.cand = null;
      this.vals = [];
    }
    if (!(fr.freq > 0) || fr.clarity < o.minClarity) return null;
    const mf = 69 + 12 * Math.log2(fr.freq / o.a4);
    const r = Math.round(mf);
    if (Math.abs(mf - r) * 100 > o.maxDevCents) {
      this.cand = null;
      this.vals = [];
      return null;
    }
    if (this.cand !== r) {
      this.cand = r;
      this.candSince = t;
      this.vals = [mf];
      return null;
    }
    this.vals.push(mf);
    if (t - this.candSince >= o.stableMs / 1000 && this.vals.length >= o.minFrames && !this.armed) {
      if (this.last !== r || this.onset) {
        this.last = r;
        this.onset = false;
        const med = median(this.vals);
        return { midi: r, cents: Math.round((med - r) * 100), freq: fr.freq, t };
      }
    }
    return null;
  }
}
